import React, { useState } from 'react';
import { Upload, AlertCircle, CheckCircle, XCircle, RefreshCw, FileText, HelpCircle, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

interface BulkEventUploadProps {
  onClose: () => void;
  onSuccess?: () => void;
}

interface UploadResult {
  success: boolean;
  summary?: {
    totalRows: number;
    created: number;
    failed: number;
  };
  results?: Array<{
    row: number;
    data: {
      title: string;
      [key: string]: any;
    };
    success: boolean;
    id?: number;
    message?: string;
    error?: string;
  }>;
  message?: string;
}

const BulkEventUpload: React.FC<BulkEventUploadProps> = ({ onClose, onSuccess }) => {
  const { token } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [results, setResults] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSampleCsv, setShowSampleCsv] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    
    if (!selectedFile) {
      return;
    }

    // Validate file type
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a valid CSV file');
      setFile(null);
      return;
    }

    // Validate file size (10MB max)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file || !token) return;

    setUploading(true);
    setUploadProgress(10);
    setProgressText('Preparing upload...');
    setError(null);
    setResults(null);

    try {
      setUploadProgress(30);
      setProgressText('Uploading file...');

      setUploadProgress(70);
      setProgressText('Processing events...');

      const result = await apiService.bulkCreateEvents(file, token);
      setUploadProgress(100);
      setProgressText('Complete!');
      setResults(result as UploadResult);
      
      // Don't auto-close the modal - let user manually close it
      // if (result.success && onSuccess) {
      //   setTimeout(() => {
      //     onSuccess();
      //   }, 3000);
      // }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

const downloadSampleCsv = () => {
    const sampleContent = `title,description,location,longitude,latitude,address,startDate,endDate,expectedAttendance,firstname,lastname,email,phone,imageUrl
Upload Test 91,Desc A,City A,77.1025,28.7041,"123 Main Street, Delhi",2025-09-25T10:00:00Z,2025-09-25T12:00:00Z,100,firstname test,lastname test,sssss@gmail.com,6666667777,https://drive.google.com/file/d/10stRvRt2oyIZfHlwQhF9560HhEUl_r4h/view?usp=drive_link`;
    const blob = new Blob([sampleContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'BulkUploadDurgaPuja.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6 sm:p-8 relative max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 border-b pb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-dpn-pink bg-opacity-10 p-2 rounded-lg">
              <Upload className="h-6 w-6 text-dpn-pink" />
            </div>
            <h2 className="font-runtime text-2xl font-bold text-gray-900">Bulk Event Creation</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Instructions */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <HelpCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-lucy-rose font-medium text-blue-800 mb-1">How to use bulk event upload</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-blue-700 font-lucy-rose">
                <li>Prepare a CSV file with event details (see sample format below)</li>
                <li>Required columns: title, description, location, startDate, endDate, expectedAttendance</li>
                <li>Optional columns: longitude, latitude, address, firstname, lastname, email, phone, imageUrl</li>
                <li>Dates should be in ISO format (YYYY-MM-DDThh:mm:ssZ)</li>
                <li>Maximum file size: 10MB</li>
              </ul>
              <div className="mt-3">
                <button 
                  onClick={() => setShowSampleCsv(!showSampleCsv)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-lucy-rose font-medium flex items-center"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  {showSampleCsv ? 'Hide sample CSV format' : 'Show sample CSV format'}
                </button>
              </div>
            </div>
          </div>

          {showSampleCsv && (
            <div className="mt-4 bg-white rounded-md p-3 overflow-x-auto">
              <pre className="text-xs text-gray-700 font-mono">
                title,description,location,longitude,latitude,address,startDate,endDate,expectedAttendance,firstname,lastname,email,phone,imageUrl<br/>
                Upload Test 91,Desc A,City A,77.1025,28.7041,"123 Main Street, Delhi",2025-09-25T10:00:00Z,2025-09-25T12:00:00Z,100,firstname test,lastname test,sssss@gmail.com,6666667777,https://drive.google.com/file/d/10stRvRt2oyIZfHlwQhF9560HhEUl_r4h/view?usp=drive_link
              </pre>
              <div className="mt-2 flex justify-end">
                <button 
                  onClick={downloadSampleCsv}
                  className="text-blue-600 hover:text-blue-800 text-xs font-lucy-rose font-medium flex items-center"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Download BulkUploadDurgaPuja.csv
                </button>
              </div>
            </div>
          )}
        </div>

        {/* File Upload Section */}
        <div className="mb-6">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              id="csv-file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              disabled={uploading}
            />
            <label 
              htmlFor="csv-file" 
              className="cursor-pointer flex flex-col items-center justify-center"
            >
              <Upload className="h-12 w-12 text-gray-400 mb-3" />
              <p className="font-lucy-rose text-gray-700 mb-1">
                {file ? file.name : 'Click to select a CSV file'}
              </p>
              <p className="font-lucy-rose text-sm text-gray-500">
                {file ? `${(file.size / 1024).toFixed(2)} KB` : 'Maximum file size: 10MB'}
              </p>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="font-lucy-rose text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="font-lucy-rose text-sm text-gray-600">{progressText}</p>
              <p className="font-lucy-rose text-sm text-gray-600">{uploadProgress}%</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-dpn-pink h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {results && (
          <div className="mb-6">
            <div className={`border rounded-lg p-4 ${
              results.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center space-x-2 mb-4">
                {results.success ? (
                  <CheckCircle className="h-6 w-6 text-green-500" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-red-500" />
                )}
                <h3 className={`font-lucy-rose font-medium ${
                  results.success ? 'text-green-700' : 'text-red-700'
                }`}>
                  {results.success ? 'Upload Complete!' : 'Upload Failed'}
                </h3>
              </div>

              {results.success && results.summary && (
                <div className="mb-4">
                  <h4 className="font-lucy-rose font-medium text-gray-700 mb-2">Summary:</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white p-3 rounded-lg text-center">
                      <p className="text-gray-500 text-sm font-lucy-rose">Total Rows</p>
                      <p className="text-xl font-medium font-runtime">{results.summary.totalRows}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-green-600 text-sm font-lucy-rose">Created</p>
                      <p className="text-xl font-medium font-runtime text-green-700">{results.summary.created}</p>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg text-center">
                      <p className="text-red-600 text-sm font-lucy-rose">Failed</p>
                      <p className="text-xl font-medium font-runtime text-red-700">{results.summary.failed}</p>
                    </div>
                  </div>
                </div>
              )}

              {results.results && results.results.length > 0 && (
                <div>
                  <h4 className="font-lucy-rose font-medium text-gray-700 mb-2">Detailed Results:</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {results.results.map((row, index) => (
                          <tr key={index} className={row.success ? 'bg-green-50' : 'bg-red-50'}>
                            <td className="px-4 py-2 text-sm font-lucy-rose text-gray-900">{row.row}</td>
                            <td className="px-4 py-2 text-sm font-lucy-rose text-gray-900">{row.data.title || 'N/A'}</td>
                            <td className="px-4 py-2">
                              {row.success ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" /> Success
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  <XCircle className="h-3 w-3 mr-1" /> Failed
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-xs font-lucy-rose text-gray-500">
                              {row.success 
                                ? `ID: ${row.id}, ${row.message || 'Created successfully'}`
                                : row.error || 'Unknown error'
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!results.success && results.message && (
                <p className="font-lucy-rose text-red-600">{results.message}</p>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => {
              if (results && results.success && onSuccess) {
                onSuccess();
              }
              onClose();
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-lucy-rose"
          >
            {results && results.success ? 'Close & Refresh Events' : 'Cancel'}
          </button>
          
          {file && !results && (
            <button
              onClick={handleUpload}
              disabled={uploading || !file}
              className="px-4 py-2 bg-dpn-pink hover:bg-dpn-raspberry text-white rounded-lg font-lucy-rose disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {uploading && <RefreshCw className="h-4 w-4 animate-spin" />}
              <span>{uploading ? 'Uploading...' : 'Upload CSV'}</span>
            </button>
          )}
          
          {results && results.success && (
            <button
              onClick={() => {
                setFile(null);
                setResults(null);
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-lucy-rose"
            >
              Upload Another File
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkEventUpload;
